import { useEffect, useRef, useState } from "react";
import { assessAlignment, type AlignmentTarget, type FaceDetector, type FaceSample, type Hint } from "../utils/faceAlignment";
import { loadFaceDetector } from "../utils/faceLandmarker";

// Tracks the agent's face against the nose target while the recorder preview is open.
// Advisory only: the recorder never blocks on it. Any failure ends in "unavailable", with a console warning only.

export type DetectorLoader = () => Promise<FaceDetector>;

export type FaceAlignmentState =
    | { status: "off" | "loading" | "unavailable" | "searching" | "good"; hint: null }
    | { status: "hint"; hint: Hint };
export type AlignmentStatus = FaceAlignmentState["status"];

interface Options {
    video: React.RefObject<HTMLVideoElement>;
    enabled: boolean;
    mirrored: boolean;
    target: AlignmentTarget;
    load?: DetectorLoader;
}

const INTERVAL_MS = 125;
// "Lined up" shows after this many good samples in a row (~0.5 s), so it does not flicker.
const GOOD_SAMPLES = 4;
const OFF: FaceAlignmentState = { status: "off", hint: null };
const UNAVAILABLE: FaceAlignmentState = { status: "unavailable", hint: null };
// A tick that sees the same thing keeps the same state object, so the recorder does not re-render 8 times a second.
const keep =
    (next: FaceAlignmentState) =>
    (prev: FaceAlignmentState): FaceAlignmentState =>
        prev.status === next.status && prev.hint === next.hint ? prev : next;

export function useFaceAlignment({ video, enabled, mirrored, target, load = loadFaceDetector }: Options): FaceAlignmentState {
    const [state, setState] = useState<FaceAlignmentState>(OFF);
    const detectorRef = useRef<FaceDetector | null>(null);
    const loadingRef = useRef<Promise<FaceDetector | null> | null>(null);
    const brokenRef = useRef(false);
    const aliveRef = useRef(true);
    // Read by the polling tick without restarting it.
    const latest = useRef({ mirrored, target });
    latest.current = { mirrored, target };

    useEffect(() => {
        if (!enabled || brokenRef.current || detectorRef.current || loadingRef.current) return;
        setState({ status: "loading", hint: null });
        // Through a promise, so a loader that throws synchronously ends the same way: unavailable.
        loadingRef.current = Promise.resolve()
            .then(load)
            .then((detector) => {
                if (!aliveRef.current) {
                    detector.close();
                    return null;
                }
                detectorRef.current = detector;
                return detector;
            })
            .catch((err: unknown) => {
                console.warn("Face tracking unavailable: the detector did not load", err);
                brokenRef.current = true;
                if (aliveRef.current) setState(UNAVAILABLE);
                return null;
            });
    }, [enabled, load]);

    useEffect(() => {
        if (!enabled) {
            setState(OFF);
            return;
        }
        if (brokenRef.current) return;
        let cancelled = false;
        let timer: ReturnType<typeof setInterval> | null = null;
        let goodStreak = 0;
        const start = (detector: FaceDetector): void => {
            if (cancelled) return;
            setState((s) => (s.status === "loading" || s.status === "off" ? { status: "searching", hint: null } : s));
            timer = setInterval(() => {
                const el = video.current;
                if (!el || el.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !el.videoWidth || !el.videoHeight) return;
                let sample: FaceSample | null;
                try {
                    sample = detector.detect(el, performance.now());
                } catch (err) {
                    console.warn("Face tracking stopped: a detection failed", err);
                    brokenRef.current = true;
                    if (timer) clearInterval(timer);
                    setState(UNAVAILABLE);
                    return;
                }
                const frame = { width: el.videoWidth, height: el.videoHeight };
                // Once "Lined up" shows, the wider hold margin applies until the face really leaves.
                const held = goodStreak >= GOOD_SAMPLES;
                const alignment = assessAlignment(sample, frame, latest.current.target, latest.current.mirrored, held);
                if (alignment.status === "good") {
                    goodStreak += 1;
                    if (goodStreak >= GOOD_SAMPLES) setState(keep({ status: "good", hint: null }));
                    return;
                }
                goodStreak = 0;
                setState(keep(alignment.status === "hint" ? { status: "hint", hint: alignment.hint } : { status: "searching", hint: null }));
            }, INTERVAL_MS);
        };
        if (detectorRef.current) start(detectorRef.current);
        else loadingRef.current?.then((detector) => detector && start(detector));
        return () => {
            cancelled = true;
            if (timer) clearInterval(timer);
        };
    }, [enabled, video]);

    // Dev re-runs this without a real unmount: StrictMode while the load is still pending (keep that load),
    // Fast Refresh after it finished (forget it, or the re-run resumes with the closed detector).
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
            if (!detectorRef.current) return;
            detectorRef.current.close();
            detectorRef.current = null;
            loadingRef.current = null;
        };
    }, []);

    return state;
}
